import { useGlobal } from "@/app/providers/use-global";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "@/components/ui/drawer";
import GroqLink from "@/components/ui/groq-link";
import Icon from "@/components/ui/icon";
import { useGlobalStats } from "@/hooks/use-global-stats";
import { reportClick } from "@/shared/lib/analytics";
import { MenuIcon } from "lucide-react";
import { useMemo } from "react";
import { useIntercom } from "react-use-intercom";
import UserMenu from "./user-menu";

export default function MobileMenu() {
  const { currentModel, setIsLoginDialogOpen, isUserLoggedIn } = useGlobal();
  const globalStatsData = useGlobalStats();
  const { show } = useIntercom();

  const totalRequestsCounter = useMemo(() => {
    return globalStatsData ? globalStatsData.data?.totalRequestsCounter : null;
  }, [globalStatsData]);

  const handleLogin = () => {
    window.localStorage.setItem("returnTo", window.location.pathname);
    setIsLoginDialogOpen(true);
  };

  return (
    <div className="md:hidden">
      <Drawer direction="right" aria-hidden="true">
        <DrawerTrigger asChild className="flex md:hidden ring-0 outline-none">
          <button type="button">
            <MenuIcon className="size-7" aria-hidden="true" />
          </button>
        </DrawerTrigger>

        <DrawerContent className="flex flex-col w-full sm:w-[320px] h-full fixed right-0 bottom-0 md:hidden rounded-none ring-0 outline-none">
          <DrawerHeader className="flex justify-between">
            <DrawerClose className="ml-auto">
              <Icon icon="close" />
            </DrawerClose>
          </DrawerHeader>

          <div className="flex flex-col p-4 gap-4 group-[.iframe]:hidden overflow-y-auto">
            <Accordion type="single" collapsible>
              <AccordionItem value="item-1" className="border-b-0">
                <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                  Products
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/pricing"
                  >
                    Pricing
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/products"
                  >
                    Products Overview
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/enterprise-access"
                  >
                    Enterprise Access
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/groqcloud"
                  >
                    GroqCloud™ Platform
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/groqrack"
                  >
                    GroqRack™ Cluster
                  </GroqLink>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border-b-0">
                <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                  Developers
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://console.groq.com/keys"
                    isExternal
                  >
                    Free API Key
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://console.groq.com/playground"
                    isExternal
                  >
                    Dev Console
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href={{ pathname: process.env.NEXT_PUBLIC_DISCORD }}
                    isExternal
                  >
                    Discord
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://console.groq.com/docs/libraries"
                    isExternal
                  >
                    Groq Libraries
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/community"
                  >
                    Community
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/showcase"
                  >
                    Showcase
                  </GroqLink>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border-b-0">
                <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                  Insights
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/inference"
                  >
                    Inference
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/blog"
                  >
                    Blog
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/events"
                  >
                    Events
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/docs"
                  >
                    GroqDocs
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/videos"
                  >
                    Videos
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/press-releases"
                  >
                    Press Releases
                  </GroqLink>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border-b-0">
                <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                  About
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/about-us"
                  >
                    About Groq
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/groq-in-the-news"
                  >
                    In the News
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href="https://groq.com/leadership"
                  >
                    Team
                  </GroqLink>
                  <GroqLink
                    className="text-content-emphasis font-normal text-sm w-full"
                    href={{ pathname: process.env.NEXT_PUBLIC_CAREERS }}
                  >
                    Careers
                  </GroqLink>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="my-2 border-t-2" />

            <div className="flex flex-col gap-4">
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_CONTACT_US }}
                isExternal
                className="text-content-emphasis font-normal text-sm w-full"
              >
                Contact Us
              </GroqLink>
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_PRIVACY_POLICY }}
                isExternal
                className="text-content-emphasis font-normal text-sm w-full"
              >
                Privacy Policy
              </GroqLink>
              <GroqLink
                href={{
                  pathname: process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS,
                }}
                isExternal
                className="text-content-emphasis font-normal text-sm w-full"
              >
                Terms of Use
              </GroqLink>
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_SECURITY }}
                isExternal
                className="text-content-emphasis font-normal text-sm w-full"
              >
                Security
              </GroqLink>
              <GroqLink
                href="https://groq.com/trademark-policy"
                isExternal
                className="text-content-emphasis font-normal text-sm w-full"
              >
                Trademark Policy
              </GroqLink>
              <GroqLink
                href="https://groq.com/cookie-policy"
                isExternal
                className="text-content-emphasis font-normal text-sm w-full"
              >
                Cookie Policy
              </GroqLink>
            </div>

            <div className="mt-2 flex justify-start items-center gap-4">
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_DISCORD }}
                isExternal
              >
                <Icon
                  className="fill-none stroke-muted-foreground hover:stroke-primaryaccent dark:stroke-white dark:hover:stroke-primaryaccent"
                  icon="discord"
                />
              </GroqLink>
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_X }}
                isExternal
              >
                <Icon
                  className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                  icon="x"
                  size="sm"
                />
              </GroqLink>
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_YOUTUBE }}
                isExternal
              >
                <Icon
                  className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                  icon="youtube"
                  height={22}
                />
              </GroqLink>
              <GroqLink href="https://www.threads.net/@groqinc" isExternal>
                <Icon
                  className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                  icon="threads"
                  size="sm"
                />
              </GroqLink>
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_LINKEDIN }}
                isExternal
              >
                <Icon
                  className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                  icon="linked-in"
                  size="sm"
                />
              </GroqLink>
              <GroqLink
                href={{ pathname: process.env.NEXT_PUBLIC_INSTAGRAM }}
                isExternal
              >
                <Icon
                  className="fill-muted-foreground hover:fill-primaryaccent dark:fill-white dark:hover:fill-primaryaccent"
                  icon="instagram"
                  size="sm"
                />
              </GroqLink>
            </div>
          </div>

          <DrawerFooter>
            <Button
              className="mt-11 justify-start p-0 hover:no-underline h-auto text-content-emphasis font-medium text-primaryaccent text-base hover:text-foreground"
              id="#support-chat-widget"
              variant="link"
              onClick={() => {
                show();
                reportClick("chat_with_us_home_mobile");
              }}
            >
              Chat with us
            </Button>

            <div className="flex flex-col">
              <h2 className="flex items-center">
                <div className="">Powered by the Groq </div>
                <div className="text-primaryaccent font-bold ml-1">LPU</div>
              </h2>
              <div className="mt-1 text-[10px] text-muted-text">
                {currentModel?.owned_by ?? "LLM"}-based chatbot. Accuracy,
                correctness or appropriateness cannot be guaranteed.
              </div>

              {totalRequestsCounter && (
                <p className="mt-2 whitespace-nowrap text-muted-text text-sm">
                  Total Requests: {totalRequestsCounter.toLocaleString("en-US")}
                </p>
              )}
            </div>

            <div className="mt-3 flex flex-col">
              {isUserLoggedIn ? (
                <UserMenu triggerVariant="outline" />
              ) : (
                <Button
                  className="justify-start"
                  variant="outline"
                  onClick={handleLogin}
                >
                  Log in
                </Button>
              )}
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
